import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface Site {
  id: string;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  tenant_id?: string;
  tenant_name?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SitesDashboard {
  total_sites: number;
  active_sites: number;
  inactive_sites: number;
}

export interface CreateSiteRequest {
  name: string;
  description?: string;
  is_active?: boolean;
  tenant_id?: string;
  metadata?: Record<string, any>;
}

export interface UpdateSiteRequest extends Partial<CreateSiteRequest> {}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({
  providedIn: 'root',
})
export class SitesService {
  private readonly apiUrl = `${environment.apiUrl}/sites`;

  constructor(private http: HttpClient) {}

  /**
   * Get all sites with their configurations
   */
  getSites(): Observable<ApiResponse<Site[]>> {
    return this.http.get<ApiResponse<Site[]>>(this.apiUrl);
  }

  /**
   * Get dashboard statistics
   */
  getDashboard(): Observable<ApiResponse<SitesDashboard>> {
    return this.http.get<ApiResponse<SitesDashboard>>(
      `${this.apiUrl}/dashboard`,
    );
  }

  /**
   * Get a single site by ID
   */
  getSite(id: string): Observable<ApiResponse<Site>> {
    return this.http.get<ApiResponse<Site>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new site
   */
  createSite(data: CreateSiteRequest): Observable<ApiResponse<Site>> {
    return this.http.post<ApiResponse<Site>>(this.apiUrl, data);
  }

  /**
   * Update an existing site
   */
  updateSite(
    id: string,
    data: UpdateSiteRequest,
  ): Observable<ApiResponse<Site>> {
    return this.http.put<ApiResponse<Site>>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete a site
   */
  deleteSite(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Toggle site active status
   */
  toggleActive(
    id: string,
  ): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
